import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PagesService } from '@services/pages.service';
import { SharedService } from '@services/shared.service';
import { PageAccessService } from 'src/app/Auth/page-access.service';
import { VoucherFieldsComponent } from './voucher-fields.component';
import {
  DEFAULT_VOUCHER_CURRENCIES,
  VOUCHER_STATUSES,
  VoucherCurrency,
  cleanParams,
  downloadCsv,
  mergeVoucherCurrencies,
  parseServerDate,
  toDateParam,
  validateVoucherRules,
} from './voucher.util';

type VoucherTab = 'batches' | 'vouchers' | 'usage' | 'budget';

interface Money {
  usd: number;
  local: number;
}

/** 2496 `budget`: the wallet in USD and in the business's own currency. */
interface VoucherBudget {
  currency: string;
  usd_to_local_rate: number;
  balance: Money;
  credited: Money;
  spent: Money;
  debited: Money;
  updated_at: string | null;
  pending_request: { request_id: string; amount: Money; note: string | null; created_at: string } | null;
}

/** Largest single request the API accepts, in USD. */
const BUDGET_REQUEST_MAX_USD = 1000000;

interface PagedState {
  page: number;
  limit: number;
  total: number;
  loading: boolean;
  /** Server message when the last load failed; null otherwise. */
  error: string | null;
}

const newPaged = (limit = 20): PagedState => ({ page: 1, limit, total: 0, loading: false, error: null });

/** Page key in dashboard_pages / the sub-user registry. */
const PAGE_KEY = 'vouchers';

/** Rows per request when walking every page for a CSV export. */
const EXPORT_PAGE_SIZE = 1000;

@Component({
  selector: 'app-vouchers',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NzTableModule,
    NzToolTipModule,
    NzDrawerModule,
    NzSelectModule,
    NzDatePickerModule,
    NzModalModule,
    NzButtonModule,
    TranslateModule,
    VoucherFieldsComponent,
  ],
  templateUrl: './vouchers.component.html',
  styleUrls: ['./vouchers.component.scss'],
})
export class VouchersComponent implements OnInit, OnDestroy {
  private pagesService = inject(PagesService);
  private sharedService = inject(SharedService);
  private pageAccess = inject(PageAccessService);
  private translate = inject(TranslateService);
  private modal = inject(NzModalService);
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  readonly statuses = VOUCHER_STATUSES;

  activeTab: VoucherTab = 'batches';

  /** May this person issue and void vouchers? The server refuses it either way. */
  get canManage(): boolean {
    return this.pageAccess.canManage(PAGE_KEY);
  }

  // ── Currencies (2383) ──
  currencies: VoucherCurrency[] = DEFAULT_VOUCHER_CURRENCIES;
  currenciesError: string | null = null;

  // ── Batches (2380) ──
  batches: any[] = [];
  batchState = newPaged();
  batchSearch = '';
  batchSource = '';

  /** Every batch (up to 100) for the filter dropdowns on the other two tabs. */
  batchOptions: { id: string; name: string }[] = [];

  // ── Vouchers (2379) ──
  vouchers: any[] = [];
  voucherState = newPaged();
  voucherSearch = '';
  voucherBatchId = '';
  voucherStatus = '';
  /** voucher_id values ticked on the current page. */
  selected = new Set<string>();

  // ── Usage (2381) ──
  usage: any[] = [];
  usageTotals: { currency: string; spent: number; forfeited: number }[] = [];
  usageState = newPaged();
  usageSearch = '';
  usageBatchId = '';
  usageRange: Date[] = [];

  // ── Create drawer ──
  createVisible = false;
  createMode: 'single' | 'bulk' = 'single';
  createForm: FormGroup = this.buildCreateForm();
  creating = false;
  createError: string | null = null;
  showCreateErrors = false;
  /** The 2378 response, shown in place of the form once vouchers exist. */
  created: { batch: any; codes: { voucher_id: string; code: string }[]; truncated: boolean } | null = null;
  codesCopied = false;
  private copiedTimer?: ReturnType<typeof setTimeout>;

  // ── Voucher budget (2496 / 2497) ──
  budget: VoucherBudget | null = null;
  budgetLoading = false;
  budgetError: string | null = null;
  budgetEntries: any[] = [];
  budgetRequests: any[] = [];
  budgetState = newPaged();

  requestVisible = false;
  requestAmount: number | null = null;
  requestNote = '';
  requestError: string | null = null;
  requestSending = false;
  withdrawing = false;

  /** Set while a void call is in flight, so a double click cannot send it twice. */
  voiding = false;
  exporting = false;

  private destroy$ = new Subject<void>();
  private batchSearch$ = new Subject<void>();
  private voucherSearch$ = new Subject<void>();
  private usageSearch$ = new Subject<void>();

  ngOnInit(): void {
    this.batchSearch$.pipe(debounceTime(400), takeUntil(this.destroy$)).subscribe(() => this.loadBatches(1));
    this.voucherSearch$.pipe(debounceTime(400), takeUntil(this.destroy$)).subscribe(() => this.loadVouchers(1));
    this.usageSearch$.pipe(debounceTime(400), takeUntil(this.destroy$)).subscribe(() => this.loadUsage(1));

    // Preserve active tab across page refresh using query parameter or localStorage
    const validTabs: VoucherTab[] = ['batches', 'vouchers', 'usage', 'budget'];
    const queryTab = this.route.snapshot.queryParams['tab'] as VoucherTab;
    const savedTab = localStorage.getItem('vouchers_active_tab') as VoucherTab;
    const initialTab: VoucherTab = validTabs.includes(queryTab)
      ? queryTab
      : validTabs.includes(savedTab)
        ? savedTab
        : 'batches';

    this.activeTab = initialTab;

    // Listen for tab query param changes if navigation occurs
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const tab = params['tab'] as VoucherTab;
      if (tab && validTabs.includes(tab) && tab !== this.activeTab) {
        this.switchTab(tab, false);
      }
    });

    this.loadCurrencies();
    this.loadBatchOptions();
    this.loadBudget(1);

    if (this.activeTab === 'batches') {
      this.loadBatches(1);
    } else if (this.activeTab === 'vouchers') {
      this.loadVouchers(1);
    } else if (this.activeTab === 'usage') {
      this.loadUsage(1);
    } else if (this.activeTab === 'budget') {
      this.loadBudget(1);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.copiedTimer) clearTimeout(this.copiedTimer);
  }

  // ───────────────────────── tabs ─────────────────────────

  switchTab(tab: VoucherTab, updateUrl = true): void {
    if (this.activeTab === tab && !updateUrl) return;
    this.activeTab = tab;
    try {
      localStorage.setItem('vouchers_active_tab', tab);
    } catch (_) {}

    if (updateUrl) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { tab },
        queryParamsHandling: 'merge',
      });
    }

    if (tab === 'batches' && !this.batches.length && !this.batchState.error) this.loadBatches(1);
    if (tab === 'vouchers' && !this.vouchers.length && !this.voucherState.error) this.loadVouchers(1);
    if (tab === 'usage' && !this.usage.length && !this.usageState.error) this.loadUsage(1);
    if (tab === 'budget') this.loadBudget(this.budgetState.page);
  }

  // ───────────────────────── loading ─────────────────────────

  private errorMessage(err: any, fallbackKey: string): string {
    return err?.error?.message || this.translate.instant(fallbackKey);
  }

  loadCurrencies(): void {
    this.pagesService.getAll('VOUCHER_CURRENCIES', {}).subscribe({
      next: (res: any) => {
        const list = Array.isArray(res) ? res : res?.currencies;
        this.currencies = mergeVoucherCurrencies(list);
        this.currenciesError = null;
      },
      error: (err) => {
        // Fall back to default currencies including EGP, USD, etc.
        this.currencies = mergeVoucherCurrencies([]);
        this.currenciesError = this.errorMessage(err, 'vouchers.errors.currenciesFailed');
      },
    });
  }

  loadBatchOptions(): void {
    this.pagesService.getAll('VOUCHER_BATCHES', { page: 1, limit: 100 }).subscribe({
      next: (res: any) => {
        const rows: any[] = Array.isArray(res?.batches) ? res.batches : [];
        this.batchOptions = rows.map((b) => ({ id: b.batch_id, name: b.name }));
      },
      // The dropdowns simply stay empty; the tables still work.
      error: () => { },
    });
  }

  loadBatches(page = this.batchState.page): void {
    const s = this.batchState;
    s.loading = true;
    s.error = null;
    s.page = page;
    const params = cleanParams({ page, limit: s.limit, source: this.batchSource, search: this.batchSearch });
    this.pagesService.getAll('VOUCHER_BATCHES', params).subscribe({
      next: (res: any) => {
        this.batches = Array.isArray(res?.batches) ? res.batches : [];
        s.total = Number(res?.totalItems) || this.batches.length;
        s.page = Number(res?.page) || page;
        s.loading = false;
      },
      error: (err) => {
        this.batches = [];
        s.total = 0;
        s.loading = false;
        s.error = this.errorMessage(err, 'vouchers.errors.loadFailed');
      },
    });
  }

  private voucherParams(page: number, limit: number): Record<string, any> {
    return cleanParams({
      page,
      limit,
      batch_id: this.voucherBatchId,
      status: this.voucherStatus,
      search: this.voucherSearch,
    });
  }

  loadVouchers(page = this.voucherState.page): void {
    const s = this.voucherState;
    s.loading = true;
    s.error = null;
    s.page = page;
    this.pagesService.getAll('VOUCHER_LIST', this.voucherParams(page, s.limit)).subscribe({
      next: (res: any) => {
        this.vouchers = Array.isArray(res?.vouchers) ? res.vouchers : [];
        s.total = Number(res?.totalItems) || this.vouchers.length;
        s.page = Number(res?.page) || page;
        s.loading = false;
        this.selected.clear();
      },
      error: (err) => {
        this.vouchers = [];
        s.total = 0;
        s.loading = false;
        s.error = this.errorMessage(err, 'vouchers.errors.loadFailed');
      },
    });
  }

  loadUsage(page = this.usageState.page): void {
    const s = this.usageState;
    s.loading = true;
    s.error = null;
    s.page = page;
    const params = cleanParams({
      page,
      limit: s.limit,
      batch_id: this.usageBatchId,
      search: this.usageSearch,
      from: toDateParam(this.usageRange?.[0]),
      to: toDateParam(this.usageRange?.[1]),
    });
    this.pagesService.getAll('VOUCHER_USAGE', params).subscribe({
      next: (res: any) => {
        this.usage = Array.isArray(res?.usage) ? res.usage : [];
        this.usageTotals = Array.isArray(res?.totals) ? res.totals : [];
        s.total = Number(res?.totalItems) || this.usage.length;
        s.page = Number(res?.page) || page;
        s.loading = false;
      },
      error: (err) => {
        this.usage = [];
        this.usageTotals = [];
        s.total = 0;
        s.loading = false;
        s.error = this.errorMessage(err, 'vouchers.errors.loadFailed');
      },
    });
  }

  // ───────────────────────── voucher budget ─────────────────────────

  loadBudget(page = this.budgetState.page): void {
    const s = this.budgetState;
    this.budgetLoading = true;
    s.loading = true;
    s.error = null;
    s.page = page;
    this.pagesService.getAll('VOUCHER_BUDGET', { page, limit: s.limit }).subscribe({
      next: (res: any) => {
        this.budget = res?.budget ?? null;
        this.budgetEntries = Array.isArray(res?.entries) ? res.entries : [];
        this.budgetRequests = Array.isArray(res?.requests) ? res.requests : [];
        s.total = Number(res?.totalItems) || this.budgetEntries.length;
        s.page = Number(res?.page) || page;
        s.loading = false;
        this.budgetLoading = false;
        // A reply without a budget is a failure too — never leave the card blank.
        this.budgetError = this.budget ? null : this.translate.instant('vouchers.budget.loadFailed');
        s.error = this.budgetError;
      },
      error: (err) => {
        s.loading = false;
        this.budgetLoading = false;
        this.budgetError = this.errorMessage(err, 'vouchers.budget.loadFailed');
        s.error = this.budgetError;
      },
    });
  }

  onBudgetPageSize(size: number): void { this.budgetState.limit = size; this.loadBudget(1); }

  get budgetCurrency(): string {
    return this.budget?.currency || 'USD';
  }

  /** The business's own currency is USD, so the local figure would only repeat the dollar one. */
  get budgetIsUsd(): boolean {
    return this.budgetCurrency === 'USD';
  }

  /** USD → the business's currency at today's rate. */
  toLocal(usd: number | null | undefined): number {
    const rate = Number(this.budget?.usd_to_local_rate) || 1;
    return Math.round(Number(usd || 0) * rate * 100) / 100;
  }

  /**
   * What the vouchers in the create form will take from the budget, in USD — the same sum the
   * server charges (face value × quantity at the currency's rate, rounded up to the cent).
   * null until the form has a value and a known currency.
   */
  get createCostUsd(): number | null {
    const v = this.createForm.getRawValue();
    const value = Number(v.voucher_value);
    const quantity = this.createMode === 'single' ? 1 : Number(v.quantity);
    const cur = this.currencies.find((c) => c.code === v.voucher_currency);
    const rate = Number(cur?.usd_rate);
    if (!(value > 0) || !(quantity >= 1) || !(rate > 0)) return null;
    const cents = Math.round((value * quantity / rate) * 100 * 100) / 100;
    return Math.ceil(cents - 1e-9) / 100;
  }

  /** The form asks for more than the budget holds. Unknown when the budget did not load. */
  get createOverBudget(): boolean {
    const cost = this.createCostUsd;
    return !!this.budget && cost !== null && cost > this.budget.balance.usd;
  }

  /** What the budget keeps after these vouchers, or null while there is no cost to show. */
  get createLeftUsd(): number | null {
    const cost = this.createCostUsd;
    if (!this.budget || cost === null || this.createOverBudget) return null;
    return Math.round((this.budget.balance.usd - cost) * 100) / 100;
  }

  get createShortfallUsd(): number {
    const cost = this.createCostUsd ?? 0;
    return Math.max(0, Math.round((cost - (this.budget?.balance.usd ?? 0)) * 100) / 100);
  }

  entryKindClass(kind: string): string {
    if (kind === 'CREDIT') return 'status-active';
    if (kind === 'DEBIT') return 'status-redeemed';
    if (kind === 'RELEASE') return 'status-partially-used';
    return 'status-expired';
  }

  requestStatusClass(status: string): string {
    if (status === 'APPROVED') return 'status-active';
    if (status === 'PENDING') return 'status-partially-used';
    if (status === 'REJECTED') return 'status-void';
    return 'status-expired';
  }

  openRequest(prefillUsd?: number): void {
    if (this.budget?.pending_request) return;
    this.requestAmount = prefillUsd && prefillUsd > 0 ? Math.ceil(prefillUsd) : null;
    this.requestNote = '';
    this.requestError = null;
    this.requestVisible = true;
  }

  closeRequest(): void {
    if (this.requestSending) return;
    this.requestVisible = false;
  }

  get requestAmountValid(): boolean {
    const n = Number(this.requestAmount);
    return this.requestAmount !== null && isFinite(n) && n >= 1 && n <= BUDGET_REQUEST_MAX_USD;
  }

  submitRequest(): void {
    if (this.requestSending) return;
    this.requestError = null;
    if (!this.requestAmountValid) {
      this.requestError = this.translate.instant('vouchers.budget.request.amountError');
      return;
    }
    if (this.requestNote.trim().length > 500) {
      this.requestError = this.translate.instant('vouchers.budget.request.noteError');
      return;
    }
    this.requestSending = true;
    const body: Record<string, any> = { amount_usd: Math.round(Number(this.requestAmount) * 100) / 100 };
    if (this.requestNote.trim()) body['note'] = this.requestNote.trim();
    this.pagesService.create('VOUCHER_BUDGET_REQUEST', body).subscribe({
      next: () => {
        this.requestSending = false;
        this.requestVisible = false;
        this.sharedService.createNotification('success', this.translate.instant('vouchers.budget.request.sent'));
        this.loadBudget(1);
      },
      error: (err) => {
        this.requestSending = false;
        this.requestError = this.errorMessage(err, 'vouchers.budget.request.failed');
        if (err?.error?.code === 'REQUEST_PENDING') this.loadBudget(1);
      },
    });
  }

  confirmWithdrawRequest(): void {
    const p = this.budget?.pending_request;
    if (!p || this.withdrawing) return;
    this.modal.confirm({
      nzTitle: this.translate.instant('vouchers.budget.withdraw.title'),
      nzContent: this.translate.instant('vouchers.budget.withdraw.content', { amount: p.amount.usd.toFixed(2) }),
      nzOkText: this.translate.instant('vouchers.budget.withdraw.confirm'),
      nzCancelText: this.translate.instant('common.cancel'),
      nzOkDanger: true,
      nzClassName: 'contest-styled-modal',
      nzCentered: true,
      nzOnOk: () => {
        this.withdrawing = true;
        this.pagesService.create('VOUCHER_BUDGET_REQUEST', { cancel_request_id: p.request_id }).subscribe({
          next: () => {
            this.withdrawing = false;
            this.sharedService.createNotification('success', this.translate.instant('vouchers.budget.withdraw.done'));
            this.loadBudget(1);
          },
          error: (err) => {
            this.withdrawing = false;
            this.sharedService.createNotification('error', this.errorMessage(err, 'vouchers.budget.withdraw.failed'));
            this.loadBudget(1);
          },
        });
      },
    });
  }

  /** From the create drawer's "not enough budget" note: ask for the shortfall. */
  requestShortfall(): void {
    this.createVisible = false;
    this.openRequest(this.createShortfallUsd);
  }

  // ───────────────────────── filters & paging ─────────────────────────

  onBatchSearch(): void { this.batchSearch$.next(); }
  onVoucherSearch(): void { this.voucherSearch$.next(); }
  onUsageSearch(): void { this.usageSearch$.next(); }

  onBatchPageSize(size: number): void { this.batchState.limit = size; this.loadBatches(1); }
  onVoucherPageSize(size: number): void { this.voucherState.limit = size; this.loadVouchers(1); }
  onUsagePageSize(size: number): void { this.usageState.limit = size; this.loadUsage(1); }

  get hasVoucherFilters(): boolean {
    return !!(this.voucherBatchId || this.voucherStatus || this.voucherSearch.trim());
  }

  get hasUsageFilters(): boolean {
    return !!(this.usageBatchId || this.usageSearch.trim() || this.usageRange?.length);
  }

  get hasBatchFilters(): boolean {
    return !!(this.batchSource || this.batchSearch.trim());
  }

  clearBatchFilters(): void {
    this.batchSource = '';
    this.batchSearch = '';
    this.loadBatches(1);
  }

  clearVoucherFilters(): void {
    this.voucherBatchId = '';
    this.voucherStatus = '';
    this.voucherSearch = '';
    this.loadVouchers(1);
  }

  clearUsageFilters(): void {
    this.usageBatchId = '';
    this.usageSearch = '';
    this.usageRange = [];
    this.loadUsage(1);
  }

  /** From a batch row: jump to its vouchers. */
  viewBatchVouchers(batch: any): void {
    this.voucherBatchId = batch.batch_id;
    this.voucherStatus = '';
    this.voucherSearch = '';
    this.switchTab('vouchers');
    this.loadVouchers(1);
  }

  viewBatchUsage(batch: any): void {
    this.usageBatchId = batch.batch_id;
    this.usageSearch = '';
    this.usageRange = [];
    this.switchTab('usage');
    this.loadUsage(1);
  }

  // ───────────────────────── row helpers ─────────────────────────

  statusClass(status: string): string {
    return 'status-' + String(status || '').toLowerCase().replace(/_/g, '-');
  }

  /** A voucher that can still be voided: nothing has finished it yet. */
  canVoid(v: any): boolean {
    return v?.status === 'ACTIVE' || v?.status === 'PARTIALLY_USED';
  }

  /** A batch with anything left to void. */
  canVoidBatch(b: any): boolean {
    return b?.status === 'ACTIVE';
  }

  /** "Single use", "Multi-use, balance carries over (up to 5 uses)" ... as an i18n key + params. */
  ruleSummary(r: { usage_type: string; carry_over_balance: boolean; max_uses: number | null }): { key: string; params: any } {
    if (r.usage_type !== 'MULTI_USE') return { key: 'vouchers.rule.single', params: {} };
    if (r.carry_over_balance) {
      return r.max_uses
        ? { key: 'vouchers.rule.carryMax', params: { max: r.max_uses } }
        : { key: 'vouchers.rule.carry', params: {} };
    }
    return { key: 'vouchers.rule.noCarry', params: { max: r.max_uses } };
  }

  usesLabel(v: any): string {
    return v.max_uses ? `${v.use_count} / ${v.max_uses}` : `${v.use_count}`;
  }

  itemName(row: any): string {
    const item = row?.order?.item;
    if (!item) return '';
    return (this.translate.currentLang === 'ar' ? item.ar : item.en) || item.en || item.ar || '';
  }

  trackById = (_: number, row: any): string => row.voucher_id || row.batch_id || row.entry_id || row.id;

  // ───────────────────────── selection ─────────────────────────

  get selectableVouchers(): any[] {
    return this.vouchers.filter((v) => this.canVoid(v));
  }

  get allSelected(): boolean {
    const rows = this.selectableVouchers;
    return rows.length > 0 && rows.every((v) => this.selected.has(v.voucher_id));
  }

  get someSelected(): boolean {
    return this.selected.size > 0 && !this.allSelected;
  }

  toggleAll(checked: boolean): void {
    this.selectableVouchers.forEach((v) => (checked ? this.selected.add(v.voucher_id) : this.selected.delete(v.voucher_id)));
  }

  toggleOne(id: string, checked: boolean): void {
    if (checked) this.selected.add(id);
    else this.selected.delete(id);
  }

  // ───────────────────────── copy ─────────────────────────

  copyCode(code: string): void {
    this.sharedService.copyToClipboard(code);
  }

  /**
   * Copy every generated code without a toast: SharedService toasts close open
   * modals, and the label change on the button is feedback enough.
   */
  async copyAllCodes(): Promise<void> {
    const text = (this.created?.codes || []).map((c) => c.code).join('\n');
    if (!text) return;
    let ok = false;
    try {
      await navigator.clipboard.writeText(text);
      ok = true;
    } catch {
      const area = document.createElement('textarea');
      area.value = text;
      area.style.position = 'fixed';
      area.style.opacity = '0';
      document.body.appendChild(area);
      area.select();
      try { ok = document.execCommand('copy'); } catch { ok = false; }
      document.body.removeChild(area);
    }
    if (ok) {
      this.codesCopied = true;
      if (this.copiedTimer) clearTimeout(this.copiedTimer);
      this.copiedTimer = setTimeout(() => (this.codesCopied = false), 2500);
    } else {
      this.createError = this.translate.instant('vouchers.create.copyFailed');
    }
  }

  // ───────────────────────── create ─────────────────────────

  private buildCreateForm(): FormGroup {
    return this.fb.group({
      name: [''],
      quantity: [1],
      voucher_value: [null],
      voucher_currency: ['USD'],
      voucher_expires_at: [null as Date | null],
      usage_type: ['SINGLE_USE'],
      carry_over_balance: [false],
      max_uses: [null as number | null],
    });
  }

  openCreate(): void {
    this.createForm = this.buildCreateForm();
    this.createMode = 'single';
    this.createError = null;
    this.showCreateErrors = false;
    this.created = null;
    this.codesCopied = false;
    this.createVisible = true;
    if (!this.currencies.length) this.loadCurrencies();
  }

  closeCreate(): void {
    if (this.creating) return;
    this.createVisible = false;
  }

  setCreateMode(mode: 'single' | 'bulk'): void {
    this.createMode = mode;
    this.createForm.patchValue({ quantity: mode === 'single' ? 1 : null });
  }

  /** Make another batch after one has just been issued. */
  createAnother(): void {
    this.openCreate();
  }

  submitCreate(): void {
    if (this.creating) return;
    const v = this.createForm.getRawValue();
    this.showCreateErrors = true;
    this.createError = null;

    const name = String(v.name ?? '').trim();
    const quantity = this.createMode === 'single' ? 1 : Number(v.quantity);
    const errors = validateVoucherRules({
      quantity,
      value: v.voucher_value === null || v.voucher_value === '' ? null : Number(v.voucher_value),
      currency: v.voucher_currency,
      expires: v.voucher_expires_at,
      usageType: v.usage_type,
      carry: v.carry_over_balance === true,
      maxUses: v.max_uses === '' ? null : v.max_uses,
    });
    if (name.length > 120) errors.push('vouchers.errors.nameLength');
    if (errors.length) {
      this.createForm.markAllAsTouched();
      if (errors.includes('vouchers.errors.nameLength')) this.createError = this.translate.instant('vouchers.errors.nameLength');
      return;
    }

    const multi = v.usage_type === 'MULTI_USE';
    const payload: Record<string, any> = {
      currency_code: v.voucher_currency,
      face_value: Number(v.voucher_value),
      quantity,
      usage_type: v.usage_type,
      carry_over_balance: multi && v.carry_over_balance === true,
      expires_at: toDateParam(v.voucher_expires_at),
    };
    if (name) payload['name'] = name;
    if (multi && v.max_uses !== null && v.max_uses !== '') payload['max_uses'] = Number(v.max_uses);

    this.creating = true;
    this.pagesService.create('VOUCHER_CREATE', payload).subscribe({
      next: (res: any) => {
        this.creating = false;
        this.created = {
          batch: res?.batch ?? null,
          codes: Array.isArray(res?.codes) ? res.codes : [],
          truncated: res?.codes_truncated === true,
        };
        // Refresh behind the drawer; the batch list is where the new batch lands.
        this.vouchers = [];
        this.voucherState.error = null;
        this.usage = [];
        this.loadBatches(1);
        this.loadBatchOptions();
        this.loadBudget(1);
        if (this.activeTab === 'vouchers') this.loadVouchers(1);
      },
      error: (err) => {
        this.creating = false;
        this.createError = this.errorMessage(err, 'vouchers.errors.createFailed');
        // Someone else spent the budget meanwhile: show the real balance next to the message.
        if (err?.error?.code === 'INSUFFICIENT_VOUCHER_BUDGET') this.loadBudget(1);
      },
    });
  }

  downloadCreatedCodes(): void {
    if (!this.created) return;
    const batchName = this.created.batch?.name || 'vouchers';
    const rows: any[][] = [['code', 'batch', 'currency', 'face_value', 'expires_at']];
    const b = this.created.batch || {};
    this.created.codes.forEach((c) => rows.push([
      c.code,
      b.name ?? '',
      b.currency ?? '',
      b.face_value ?? '',
      b.expires_at ? String(b.expires_at).slice(0, 10) : '',
    ]));
    downloadCsv(this.fileName(batchName), rows);
  }

  // ───────────────────────── void ─────────────────────────

  confirmVoidVoucher(v: any): void {
    this.confirmVoid({
      title: 'vouchers.void.titleOne',
      content: 'vouchers.void.contentOne',
      params: { code: v.code },
      body: { voucher_id: v.voucher_id },
    });
  }

  confirmVoidSelected(): void {
    if (!this.selected.size) return;
    this.confirmVoid({
      title: 'vouchers.void.titleMany',
      content: 'vouchers.void.contentMany',
      params: { count: this.selected.size },
      body: { voucher_ids: Array.from(this.selected) },
    });
  }

  confirmVoidBatch(b: any): void {
    const left = Math.max(0, (b.counts?.issued || 0) - (b.counts?.redeemed || 0) - (b.counts?.voided || 0));
    this.confirmVoid({
      title: 'vouchers.void.titleBatch',
      content: 'vouchers.void.contentBatch',
      params: { name: b.name, count: left },
      body: { batch_id: b.batch_id },
    });
  }

  private confirmVoid(o: { title: string; content: string; params: any; body: Record<string, any> }): void {
    if (this.voiding) return;
    this.modal.confirm({
      nzTitle: this.translate.instant(o.title),
      nzContent: this.translate.instant(o.content, o.params),
      nzOkText: this.translate.instant('vouchers.void.confirm'),
      nzCancelText: this.translate.instant('common.cancel'),
      nzOkDanger: true,
      nzClassName: 'contest-styled-modal',
      nzCentered: true,
      nzOnOk: () => this.runVoid(o.body),
    });
  }

  private runVoid(body: Record<string, any>): void {
    this.voiding = true;
    this.pagesService.create('VOUCHER_VOID', body).subscribe({
      next: (res: any) => {
        this.voiding = false;
        const n = Number(res?.voided ?? 0);
        const released = Number(res?.budget_released_usd ?? 0);
        this.sharedService.createNotification('success', released > 0
          ? this.translate.instant('vouchers.void.doneReleased', { count: n, amount: released.toFixed(2) })
          : this.translate.instant('vouchers.void.done', { count: n }));
        this.selected.clear();
        this.loadBudget(1);
        this.loadBatches(this.batchState.page);
        this.loadVouchers(this.voucherState.page);
        if (this.usage.length) this.loadUsage(this.usageState.page);
      },
      error: (err) => {
        this.voiding = false;
        this.sharedService.createNotification('error', this.errorMessage(err, 'vouchers.void.failed'));
      },
    });
  }

  // ───────────────────────── CSV export ─────────────────────────

  private fileName(base: string): string {
    const safe = String(base || 'vouchers').replace(/[^\w\-]+/g, '_').replace(/^_+|_+$/g, '') || 'vouchers';
    return `${safe}_${toDateParam(new Date())}.csv`;
  }

  /** Walk every page of 2379 for the given filters. */
  private async fetchAllVouchers(extra: Record<string, any>): Promise<any[]> {
    const all: any[] = [];
    let page = 1;
    for (;;) {
      const res: any = await firstValueFrom(
        this.pagesService.getAll('VOUCHER_LIST', cleanParams({ ...extra, page, limit: EXPORT_PAGE_SIZE })),
      );
      const rows: any[] = Array.isArray(res?.vouchers) ? res.vouchers : [];
      all.push(...rows);
      if (!res?.hasNextPage || !rows.length) break;
      page = Number(res?.nextPage) || page + 1;
    }
    return all;
  }

  private voucherCsv(rows: any[]): any[][] {
    const head = [
      'code', 'status', 'currency', 'face_value', 'remaining_value', 'spent', 'use_count', 'max_uses',
      'usage_type', 'carry_over_balance', 'batch', 'contest', 'assigned_to', 'assigned_at',
      'expires_at', 'first_used_at', 'last_used_at', 'redeemed_at', 'voided_at', 'created_at',
    ];
    return [head, ...rows.map((v) => [
      v.code, v.status, v.currency, v.face_value, v.remaining_value, v.spent, v.use_count, v.max_uses ?? '',
      v.usage_type, v.carry_over_balance ? 'yes' : 'no', v.batch?.name ?? '', v.contest?.name ?? '',
      v.assigned_to?.name ?? (v.assigned_to ? v.assigned_to.id : ''), v.assigned_at ?? '',
      v.expires_at ?? '', v.first_used_at ?? '', v.last_used_at ?? '', v.redeemed_at ?? '', v.voided_at ?? '', v.created_at ?? '',
    ])];
  }

  private async runExport(fileBase: string, filters: Record<string, any>): Promise<void> {
    if (this.exporting) return;
    this.exporting = true;
    this.sharedService.showLoader();
    try {
      const rows = await this.fetchAllVouchers(filters);
      if (!rows.length) {
        this.sharedService.createNotification('error', this.translate.instant('vouchers.export.empty'));
        return;
      }
      downloadCsv(this.fileName(fileBase), this.voucherCsv(rows));
    } catch (err: any) {
      this.sharedService.showError(err, this.translate.instant('vouchers.export.failed'));
    } finally {
      this.sharedService.hideLoader();
      this.exporting = false;
    }
  }

  /** The Vouchers tab export: everything matching the current filters, all pages. */
  exportVouchers(): void {
    const batch = this.batchOptions.find((b) => b.id === this.voucherBatchId);
    this.runExport(batch?.name || 'vouchers', {
      batch_id: this.voucherBatchId,
      status: this.voucherStatus,
      search: this.voucherSearch,
    });
  }

  exportBatchCodes(b: any): void {
    this.runExport(b.name, { batch_id: b.batch_id });
  }

  /** The Usage tab export: the ledger rows for the current filters. */
  async exportUsage(): Promise<void> {
    if (this.exporting) return;
    this.exporting = true;
    this.sharedService.showLoader();
    try {
      const rows: any[] = [];
      let page = 1;
      for (;;) {
        const res: any = await firstValueFrom(this.pagesService.getAll('VOUCHER_USAGE', cleanParams({
          page,
          limit: EXPORT_PAGE_SIZE,
          batch_id: this.usageBatchId,
          search: this.usageSearch,
          from: toDateParam(this.usageRange?.[0]),
          to: toDateParam(this.usageRange?.[1]),
        })));
        const part: any[] = Array.isArray(res?.usage) ? res.usage : [];
        rows.push(...part);
        if (!res?.hasNextPage || !part.length) break;
        page = Number(res?.nextPage) || page + 1;
      }
      if (!rows.length) {
        this.sharedService.createNotification('error', this.translate.instant('vouchers.export.empty'));
        return;
      }
      const head = [
        'date', 'kind', 'code', 'batch', 'currency', 'amount', 'forfeited', 'balance_before', 'balance_after',
        'order_reference', 'item', 'order_total', 'order_currency',
      ];
      downloadCsv(this.fileName('voucher-usage'), [head, ...rows.map((r) => [
        r.created_at, r.kind, r.code, r.batch_name, r.currency, r.amount, r.forfeited, r.balance_before,
        r.balance_after, r.order?.reference ?? '', this.itemName(r), r.order?.total ?? r.order_amount ?? '',
        r.order?.currency ?? r.order_currency ?? '',
      ])]);
    } catch (err: any) {
      this.sharedService.showError(err, this.translate.instant('vouchers.export.failed'));
    } finally {
      this.sharedService.hideLoader();
      this.exporting = false;
    }
  }

  /** For the template: a server expiry as the calendar day it was set to. */
  expiryDay(iso: string): Date | null {
    return parseServerDate(iso);
  }
}
